import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import {
  OrganizerSubscription,
  SubscriptionPlanId,
  SubscriptionStatus,
  SUBSCRIPTION_PLANS,
  getPlanById,
  getDefaultSubscription,
  isPro
} from '../types/subscription.types';
import stripeService from '../supabase/stripe.service';

// ===== STORE STATE INTERFACE =====
interface SubscriptionState {
  // State
  subscription: OrganizerSubscription | null;
  loading: boolean;
  error: string | null;
  lastFetchTime: number | null;

  // Actions
  loadSubscription: (organizerId: string) => Promise<void>;
  refreshSubscription: () => Promise<void>;
  setSubscription: (subscription: OrganizerSubscription | null) => void;
  updateSubscription: (data: Partial<OrganizerSubscription>) => void;
  clearSubscription: () => void;
  incrementEventsUsed: () => void;
  decrementEventsUsed: () => void;

  // Getters
  canCreateEvent: () => boolean;
  getEventsRemaining: () => number | null;
  getCurrentPlan: () => typeof SUBSCRIPTION_PLANS[0];
  isPro: () => boolean;
}

// ===== DEBOUNCE FLAGS =====
let isLoadingSubscription = false;
const LOAD_SUBSCRIPTION_DEBOUNCE_MS = 1000;

// ===== STORE IMPLEMENTATION =====
export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set, get) => ({
      // ===== INITIAL STATE =====
      subscription: null,
      loading: false,
      error: null,
      lastFetchTime: null,

      // ===== ACTIONS =====

      /**
       * Load subscription for an organizer from Supabase
       * Fetches real subscription data via stripeService
       */
      loadSubscription: async (organizerId: string) => {
        const now = Date.now();
        const { lastFetchTime } = get();

        // Debounce protection
        if (isLoadingSubscription) return;
        if (lastFetchTime && now - lastFetchTime < LOAD_SUBSCRIPTION_DEBOUNCE_MS) return;

        try {
          isLoadingSubscription = true;
          set({ loading: true, error: null });

          // Fetch subscription from Supabase via stripe service
          const subscriptionInfo = await stripeService.getSubscriptionInfo(organizerId);

          if (subscriptionInfo) {
            const subscription: OrganizerSubscription = {
              organizerId,
              planId: subscriptionInfo.planId as SubscriptionPlanId,
              status: subscriptionInfo.status as SubscriptionStatus,
              currentPeriodEnd: subscriptionInfo.currentPeriodEnd || new Date(),
              cancelAtPeriodEnd: subscriptionInfo.cancelAtPeriodEnd,
              eventsUsed: subscriptionInfo.eventsUsed,
              eventsLimit: subscriptionInfo.eventsLimit,
              // stripeCustomerId removed - not needed in frontend
            };

            set({
              subscription,
              loading: false,
              lastFetchTime: now
            });
          } else {
            // No subscription found, use default free
            const defaultSub = getDefaultSubscription(organizerId);
            set({
              subscription: defaultSub,
              loading: false,
              lastFetchTime: now
            });
          }

          isLoadingSubscription = false;
        } catch (error) {
          console.error('SubscriptionStore - loadSubscription error:', error);
          // Fallback to default subscription on error
          const defaultSub = getDefaultSubscription(organizerId);
          set({
            subscription: defaultSub,
            error: error instanceof Error ? error.message : 'Failed to load subscription',
            loading: false
          });
          isLoadingSubscription = false;
        }
      },

      /**
       * Refresh subscription data (after payment, etc.)
       */
      refreshSubscription: async () => {
        const { subscription } = get();
        if (!subscription?.organizerId) return;

        // Force refresh by clearing lastFetchTime
        set({ lastFetchTime: null });
        await get().loadSubscription(subscription.organizerId);
      },

      /**
       * Set subscription directly (for updates from payment providers)
       */
      setSubscription: (subscription: OrganizerSubscription | null) => {
        set({ subscription, error: null });
      },

      /**
       * Update subscription with partial data
       */
      updateSubscription: (data: Partial<OrganizerSubscription>) => {
        const { subscription } = get();
        if (!subscription) return;

        set({
          subscription: {
            ...subscription,
            ...data,
            updatedAt: new Date()
          }
        });
      },

      /**
       * Clear subscription (on logout)
       */
      clearSubscription: () => {
        set({
          subscription: null,
          loading: false,
          error: null,
          lastFetchTime: null
        });
      },

      /**
       * Increment events used (after publishing an event)
       */
      incrementEventsUsed: () => {
        const { subscription } = get();
        if (!subscription) return;

        set({
          subscription: {
            ...subscription,
            eventsUsed: subscription.eventsUsed + 1,
            updatedAt: new Date()
          }
        });
      },

      /**
       * Decrement events used (after deleting an event)
       */
      decrementEventsUsed: () => {
        const { subscription } = get();
        if (!subscription || subscription.eventsUsed <= 0) return;

        set({
          subscription: {
            ...subscription,
            eventsUsed: Math.max(0, subscription.eventsUsed - 1),
            updatedAt: new Date()
          }
        });
      },

      // ===== GETTERS =====

      /**
       * Check if organizer can create a new event
       */
      canCreateEvent: (): boolean => {
        const { subscription } = get();
        if (!subscription) return false;

        // Pro plans have unlimited events
        if (isPro(subscription.planId)) return true;

        // Check against limit
        if (subscription.eventsLimit === null) return true;
        return subscription.eventsUsed < subscription.eventsLimit;
      },

      /**
       * Get number of events remaining (null = unlimited)
       */
      getEventsRemaining: (): number | null => {
        const { subscription } = get();
        if (!subscription) return 0;

        // Pro plans = unlimited
        if (isPro(subscription.planId)) return null;

        // Free plan or pay-per-event
        if (subscription.eventsLimit === null) return null;
        return Math.max(0, subscription.eventsLimit - subscription.eventsUsed);
      },

      /**
       * Get the current plan details
       */
      getCurrentPlan: () => {
        const { subscription } = get();
        if (!subscription) return SUBSCRIPTION_PLANS[0]; // Default to free
        return getPlanById(subscription.planId);
      },

      /**
       * Check if current plan is Pro (monthly or yearly)
       */
      isPro: (): boolean => {
        const { subscription } = get();
        if (!subscription) return false;
        return isPro(subscription.planId);
      }
    }),
    {
      name: 'subscription-store',
      // Only persist essential subscription data
      partialize: (state) => ({
        subscription: state.subscription
      }),
      version: 1
    }
  )
);

// ===== CONVENIENCE HOOKS =====

/**
 * Hook for subscription data and actions
 */
export const useSubscription = () => useSubscriptionStore(
  useShallow((state) => ({
    subscription: state.subscription,
    loading: state.loading,
    error: state.error,
    loadSubscription: state.loadSubscription,
    refreshSubscription: state.refreshSubscription,
    setSubscription: state.setSubscription,
    updateSubscription: state.updateSubscription,
    clearSubscription: state.clearSubscription
  }))
);

/**
 * Hook for event limits and checks
 */
export const useEventLimits = () => useSubscriptionStore(
  useShallow((state) => ({
    canCreateEvent: state.canCreateEvent,
    getEventsRemaining: state.getEventsRemaining,
    incrementEventsUsed: state.incrementEventsUsed,
    decrementEventsUsed: state.decrementEventsUsed,
    eventsUsed: state.subscription?.eventsUsed ?? 0,
    eventsLimit: state.subscription?.eventsLimit ?? 5
  }))
);

/**
 * Hook for plan information
 */
export const usePlanInfo = () => useSubscriptionStore(
  useShallow((state) => ({
    plan: state.getCurrentPlan(),
    planId: state.subscription?.planId ?? 'free',
    isPro: state.isPro(),
    status: state.subscription?.status ?? 'active',
    renewalDate: state.subscription?.currentPeriodEnd
  }))
);
